/// <amd-module name="Dashboards/WidgetCatalogUI/WidgetCatalogView" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import Draggable, { DraggableData } from "react-draggable";
import { caseInsensitiveContains, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { PanelContent, PanelFooter } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { Filter, FILTER_CHANGE_EVENT, FILTER_RESET_EVENT, FilterOperatorType } from "VSSUI/Utilities/Filter";

import { WidgetCatalogInformation, WidgetCatalogQuery } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import * as Resources from "Dashboards/WidgetCatalogUI/Resources";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { WidgetCatalogProps } from "Dashboards/WidgetContracts/Internal/WidgetCatalogProps";
import { CatalogViewUtilities } from "./Utilities";
import { WidgetCatalogEntryItem, WidgetList } from "./WidgetList";

import * as Fuse from "Fuse";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { TextField } from "VSSUI/TextField";
const fuzzySearchFeatureID = "ms.vss-dashboards.dashboard-widget-catalog-fuzzy-search-feature";

const enum CatalogAddMode {
    PanelButtonClick,
    ItemActivate,
    ItemDrag
}

/** Defines the key and its associated weight used to search the Widget Catalog using the Fuse.js library. */
export interface searchKey {
    name: keyof WidgetCatalogEntry;
    weight: number;
}

/** Defines the set of keys used to search the Widget Catalog. Search results are affected by how the keys are weighted.
 *  Widget name is the highest weighted search key. */
const catalogSearchKeys: searchKey[] = [
    { name: "name", weight: 0.4 },
    { name: "description", weight: 0.3 },
    { name: "publisherName", weight: 0.1 },
    { name: "tags", weight: 0.1 },
    { name: "keywords", weight: 0.1 }
];

/** Defines the set of options required to search the Widget Catalog using the Fuse.js library. */
export const catalogSearchOptions: Fuse.FuseOptions<WidgetCatalogEntry> = {
    minMatchCharLength: 1,
    shouldSort: true,
    tokenize: true,
    matchAllTokens: true,
    threshold: 0.4,
    location: 0,
    distance: 75,
    maxPatternLength: 32,
    keys: catalogSearchKeys
};

export interface CatalogViewState {
    catalogInformation: WidgetCatalogInformation | undefined;
    selectedItem: WidgetCatalogEntry | undefined;
    showDraggableElement: boolean;
    displayWidgets: WidgetCatalogEntry[]; // Widgets that are visible in the catalog when it is rendered.
    filterText: string;
}

/** This panel is used to allow a user to choose a widget. */
export class WidgetCatalogView extends VssComponent<WidgetCatalogProps, CatalogViewState> {
    private draggableRef = React.createRef<Draggable>();
    private handOffDragEvent: MouseEvent | undefined;
    private searchOptions: Fuse.FuseOptions<WidgetCatalogEntry>; // Search options to perform a Fuse search.
    private fuseSearch: Fuse<WidgetCatalogEntry, Fuse.FuseOptions<WidgetCatalogEntry>> | undefined; // The Fuse object used to perform search operations.
    private telemetryService: IVssTelemetryService; // To publish telemetry events.
    private filter: Filter;
    private filterDropdown: DropdownMultiSelection;
    private dropdownItems: Array<IListBoxItem<{}>>;
    private SUGGESTED_TAGS_LIMIT: number;

    constructor(props: WidgetCatalogProps, context: IVssComponentContext) {
        super(props, context);

        this.filter = new Filter();
        this.filter.setState({ tagsSelected: { value: [], operator: FilterOperatorType.and }, filterText: { value: "" } });
        this.filterDropdown = new DropdownMultiSelection();
        this.SUGGESTED_TAGS_LIMIT = 5;

        this.searchOptions = catalogSearchOptions;
        this.telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        this.state = {
            selectedItem: undefined,
            catalogInformation: undefined, // catalogEntries must be sorted alphabetically.
            showDraggableElement: false,
            displayWidgets: [],
            filterText: ""
        };
    }

    public async componentDidMount(): Promise<void> {
        const queryResult = await this.getCatalogEntries();

        this.filter.subscribe(this.onFilterChange, FILTER_CHANGE_EVENT);
        this.filter.subscribe(this.onDismissClicked, FILTER_RESET_EVENT);
        this.filterDropdown.subscribe(this.onFilterChange);
        this.fuseSearch = new Fuse(queryResult.catalogEntries, this.searchOptions);
        this.onOpenCatalog();
        this.setState({ catalogInformation: queryResult, displayWidgets: queryResult.catalogEntries });
    }

    public componentWillUnmount(): void {
        this.filter.unsubscribe(this.onFilterChange);
        this.filter.unsubscribe(this.onDismissClicked);
    }

    public render(): JSX.Element | null {
        /*  searchExperience is the Filter UI component powered by a fuzzy search algorithm if the feature is enabled, 
            else it is the existing Text Field component powered by a simple substring search.
        */
        const searchExperience = this.getSearchExperience();
        return (
            <>
                <CustomHeader className="bolt-header-with-commandbar">
                    <HeaderTitleArea>
                        <div className="flex-grow scroll-hidden widget-catalog-header">
                            <div className="widget-catalog-header-text fontSizeL">{Resources.WidgetCatalog_HeaderText}</div>
                            {this.state.catalogInformation && searchExperience}
                        </div>
                    </HeaderTitleArea>
                </CustomHeader>
                <PanelContent>
                    {!this.state.catalogInformation && <Spinner className="catalog-spinner" />}
                    {this.state.catalogInformation && (
                        <WidgetList
                            key={`WidgetList-${this.filter.getFilterItemValue("filterText")}`} // Re-render on filter changes, to ensure selection state is not preserved in control
                            onDrag={this.onListDrag}
                            selectedItem={this.state.selectedItem}
                            onSelect={this.onSelect}
                            onActivate={this.onActivate}
                            widgetCatalogEntries={this.state.displayWidgets}
                            searchTextPresent={
                                this.filter.getFilterItemValue("filterText") || this.filter.getFilterItemValue<string[]>("tagsSelected")!.length != 0
                            }
                        />
                    )}
                </PanelContent>
                <PanelFooter showSeparator>
                    {this.state.catalogInformation && this.renderGalleryLinkBlurb(this.state.catalogInformation.galleryUrl)}
                    <ButtonGroup className="bolt-panel-footer-buttons flex-grow">
                        <Button
                            onClick={this.onAddButton}
                            disabled={this.state.selectedItem == null}
                            text={Resources.WidgetCatalog_Add}
                            primary={true}
                        />
                    </ButtonGroup>
                </PanelFooter>
                <Draggable ref={this.draggableRef} onDrag={this.onDrag} onStop={this.onStopDrag}>
                    <div className="draggable-widget-entry" style={{ display: this.state.showDraggableElement ? "block" : "none" }}>
                        {this.state.selectedItem && <WidgetCatalogEntryItem widgetCatalogEntry={this.state.selectedItem} isSelected={false} />}
                    </div>
                </Draggable>
            </>
        );
    }

    public renderGalleryLinkBlurb(linkUrl: string): JSX.Element | null {
        return (
            <FormatComponent format={Resources.WidgetCatalog_GalleryLink}>
                <Link href={linkUrl} target="_blank">
                    {Resources.WidgetCatalog_GalleryLinkDestination}
                </Link>
            </FormatComponent>
        );
    }

    /**
     * Creates second event for draggable, moving it to the actual mouse posision
     */
    public componentDidUpdate() {
        if (this.handOffDragEvent !== undefined && this.draggableRef.current) {
            const draggableElement = ReactDOM.findDOMNode(this.draggableRef.current) as Element;
            CatalogViewUtilities.finishHandOffDrag(this.handOffDragEvent, draggableElement);

            // Null out the original passed in event, so we only execute the above flow once
            this.handOffDragEvent = undefined;
        }
    }

    private async getCatalogEntries(): Promise<WidgetCatalogInformation> {
        const queryCache = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const query = new WidgetCatalogQuery(this.props.projectId);
        const queryResult = { ...(await queryCache.getCacheableQueryResult(query)) };

        // Hide items which are not visible from catalog UI.
        queryResult.catalogEntries = queryResult.catalogEntries.filter(items => {
            return items.isVisibleFromCatalog;
        });
        // Sort the widget entries alphabetically for use by the Catalog UI
        queryResult.catalogEntries = queryResult.catalogEntries.sort((a, b) => {
            return localeIgnoreCaseComparer(a.name, b.name);
        });

        return queryResult;
    }

    /** Returns the search component to be rendered according to the feature flag.
     *  If the fuzzy search feature is enabled, a Filter UI component powered by fuzzy search
     *  is returned, else the existing Text Field component powered by a basic substring
     *  search is returned.
     */
    private getSearchExperience() {
        let searchExperience;
        this.dropdownItems = this.getFilterTags();
        if (isFeatureEnabled(this.context.pageContext, fuzzySearchFeatureID, false)) {
            searchExperience = (
                <FilterBar filter={this.filter} className="widget-catalog-filter" onDismissClicked={this.onDismissClicked}>
                    <KeywordFilterBarItem filterItemKey={"filterText"} />
                    <DropdownFilterBarItem
                        filterItemKey="tagsSelected"
                        filter={this.filter}
                        items={this.dropdownItems}
                        selection={this.filterDropdown}
                        placeholder={Resources.WidgetCatalog_FilterDropDownButtonPlaceholder}
                        noItemsText={Resources.WidgetCatalog_FilterDropDownNoItems}
                        filterPlaceholderText={Resources.WidgetCatalog_FilterDropDownFilterPlaceholder}
                        filteredNoResultsText={Resources.WidgetCatalog_FilterDropDownFilterNoResults}
                    />
                </FilterBar>
            );
        } else {
            searchExperience = (
                <TextField
                    className="widget-catalog-filter"
                    value={this.state.filterText}
                    onChange={this.onTextChange}
                    placeholder={Resources.WidgetCatalog_SearchWatermark}
                    prefixIconProps={{ iconName: "Search" }}
                />
            );
        }
        return searchExperience;
    }

    private filterCatalogEntries(searchText: string): WidgetCatalogEntry[] {
        if (searchText === "") {
            return this.state.catalogInformation!.catalogEntries;
        }
        const filteredEntries = this.state.catalogInformation!.catalogEntries.filter((entry: WidgetCatalogEntry) => {
            return this.isEntryAMatchForFilter(entry, searchText);
        });
        return filteredEntries;
    }

    /** Returns filtered catalog entries using the Fuse 3.4.4 library to implement Fuzzy Search. */
    private filterCatalogEntriesFuzzy(): WidgetCatalogEntry[] {
        const searchText = this.filter.getFilterItemValue<string>("filterText");
        const entries = this.state.catalogInformation!.catalogEntries;
        if (searchText) {
            return searchText.trim() === "" ? entries : this.fuseSearch!.search(searchText.trim());
        } else {
            return entries;
        }
    }

    private onListDrag = (e: MouseEvent, catalogRow: React.RefObject<HTMLDivElement>, selectedItem: WidgetCatalogEntry): void => {
        if (!this.draggableRef.current || !catalogRow.current) {
            return;
        }

        const draggableElement = ReactDOM.findDOMNode(this.draggableRef.current) as HTMLElement;
        const catalogRowElement = ReactDOM.findDOMNode(catalogRow.current) as HTMLElement;

        CatalogViewUtilities.handOffDrag(e, draggableElement, catalogRowElement);

        this.setState({
            showDraggableElement: true,
            selectedItem: selectedItem
        });

        // React synthetic events lose data, persist it so we can access it in componentDidUpdate
        (e as any).persist();
        this.handOffDragEvent = e;
    };

    private onDrag = (e: MouseEvent, data: DraggableData): false | void => {
        if (this.state.selectedItem) {
            const configSettings = generateWidgetConfigFromCatalog(this.state.selectedItem);

            return CatalogViewUtilities.handleDrag(e, configSettings, this.props);
        }
    };

    private onStopDrag = (e: MouseEvent, data: DraggableData): void => {
        this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.StopDragWidget", {
            timeAtDrag: performance.now(),
            isNewPlatform: true
        });
        this.stopDrag(e, data);
    };

    private onAddButton = (): void => {
        this.onAdd(CatalogAddMode.PanelButtonClick);
    };

    private onActivate = (): void => {
        this.onAdd(CatalogAddMode.ItemActivate);
    };

    private onAdd(addMode: CatalogAddMode): void {
        if (this.state.selectedItem) {
            const configSettings = generateWidgetConfigFromCatalog(this.state.selectedItem);
            this.props.onAdd(configSettings);

            // Note: This event has parity to old platform:
            const properties = {
                isNewPlatform: true,
                contributionId: this.state.selectedItem.contributionId,
                "addMode:": addMode.toString()
            };
            this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.Add", properties);
        }
    }

    private onSelect = (widget: WidgetCatalogEntry): void => {
        this.setState({
            selectedItem: widget
        });
    };

    private onOpenCatalog(): void {
        // Note: This event has parity to old platform:
        this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.Open", { isNewPlatform: true });
    }

    /** Updates filter state, and reset selection state */
    private onFilterChange = (): void => {
        this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.StartSearch", {
            timeAtSearch: performance.now(),
            isNewPlatform: true
        });
        const filtersApplied = this.filter.getFilterItemValue<string[]>("tagsSelected");
        let filteredEntries = this.filterCatalogEntriesFuzzy();
        if (filtersApplied && filtersApplied.length != 0) {
            filteredEntries = this.onTagSelection(filteredEntries); // Filtering with the search text and the tags selected.
        }
        this.setState({
            selectedItem: undefined,
            displayWidgets: filteredEntries
        });
    };

    /** Returns widgets whose tags match those selected in the tags dropdown in the filter. */
    private onTagSelection = (entries: WidgetCatalogEntry[]): WidgetCatalogEntry[] => {
        const tags: string[] = this.filter.getFilterItemValue<string[]>("tagsSelected")!;
        if (tags != undefined) {
            this.publishFilterTelemetry();
            return this.filterWithTags(entries, tags);
        }
        return entries;
    };

    /** Returns widgets whose tags include all the selected tags in the dropdown. */
    private filterWithTags(widgets: WidgetCatalogEntry[], tags: string[]): WidgetCatalogEntry[] {
        const filteredEntries: WidgetCatalogEntry[] = [];
        for (let i = 0; i < widgets.length; i++) {
            if (widgets[i].tags != undefined && this.setContainsAny(widgets[i].tags!, tags)) {
                filteredEntries.push(widgets[i]);
            }
        }
        return filteredEntries;
    }

    /** Returns true if any element of [subset] is in [set]. */
    private setContainsAny<T>(set: T[], subset: T[]): boolean {
        for (let i = 0; i < subset.length; i++) {
            if (set.indexOf(subset[i]) != -1) {
                return true;
            }
        }
        return false;
    }

    /** Clears search text and applied filters in the filterbar. */
    private onDismissClicked = (): void => {
        this.filter.setState({ tagsSelected: { value: [], operator: FilterOperatorType.and }, filterText: { value: "" } });
    };

    /** Updates filter state, and reset selection state */
    private onTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.StartSearch", {
            timeAtSearch: performance.now(),
            isNewPlatform: true
        });
        this.setState({
            filterText: value,
            selectedItem: undefined,
            displayWidgets: this.filterCatalogEntries(value.trim())
        });
    };

    /** Indicates if the supplied catalog entry satisfies the current filter state. */
    private isEntryAMatchForFilter(entry: WidgetCatalogEntry | undefined, filterText: string): boolean {
        return entry != null && (caseInsensitiveContains(entry.name, filterText) || caseInsensitiveContains(entry.description, filterText));
    }

    //#region Drag handling

    private stopDrag(e: MouseEvent, data: DraggableData): void {
        // Hiding draggable when drag stops or we drop the item into grid
        this.setState({
            showDraggableElement: false
        });
    }

    /** Create and send events to the telemetry service endpoint. */
    private publishTelemetryEvent(area: string, feature: string, properties: { [key: string]: any }): void {
        this.telemetryService.publishEvent(area, feature, properties);
    }

    /** Records the filters selected in the filterbar. */
    private publishFilterTelemetry(): void {
        this.publishTelemetryEvent("Dashboards", "Dashboards.Catalog.Filters", {
            tagsSelected: this.filter.getFilterItemValue("tagsSelected"),
            isNewPlatform: true
        });
    }

    /** Returns the tags of all the widgets in the catalog. */
    private getFilterTags(): Array<IListBoxItem<{}>> {
        const entries = this.state.catalogInformation ? this.state.catalogInformation.catalogEntries : [];
        if (entries.length === 0) {
            return [{ id: "no tags", text: Resources.WidgetCatalog_FilterDropDownNoItems, type: ListBoxItemType.Header }];
        }

        const tagsLists = new Array<string[]>();
        entries.forEach(entry => {
            // Accumulating the list of tags for each widget.
            if (entry.tags) {
                tagsLists.push(entry.tags);
            }
        });

        // Flattening the list and sorting alphabetically.
        const flatTags = tagsLists.reduce((acc, val) => acc.concat(val), []).sort(localeIgnoreCaseComparer);

        // Removing duplicate tags.
        const tags = new Set(flatTags);

        // Counting the tags that appear most often in the catalog for the suggested tags category.
        const tagCount: { key: string; count: number }[] = [];
        tags.forEach(tag => {
            tagCount.push({ key: tag, count: flatTags.filter(t => t === tag).length });
        });
        const suggested = tagCount.sort((pair1, pair2) => pair2.count - pair1.count).slice(0, this.SUGGESTED_TAGS_LIMIT); // Sorting in descending order and taking first five.

        // Converting to dropdown items.
        const dropdownItems: Array<IListBoxItem<{}>> = [];
        dropdownItems.unshift({ id: "suggested", text: Resources.WidgetCatalog_FilterDropDownFirstHeader, type: ListBoxItemType.Header }); // Suggested tags are the top five that appear most often in the catalog.
        suggested.forEach(pair => {
            dropdownItems.push({ id: pair.key, text: pair.key });
            tags.delete(pair.key);
        });
        dropdownItems.push({ id: "all", text: Resources.WidgetCatalog_FilterDropDownSecondHeader, type: ListBoxItemType.Header }); // All tags do not contain suggested tags.
        tags.forEach(tag => {
            dropdownItems.push({ id: tag, text: tag });
        });

        return dropdownItems;
    }
}

/**
 * After the catalog identifies a user-selected entry, the Dashboard needs initial state for persistence and rendering to dashboard.
 * @param widgetCatalogEntry
 */
export function generateWidgetConfigFromCatalog(widgetCatalogEntry: WidgetCatalogEntry): DashboardWidgetProps {
    return {
        id: undefined,
        name: widgetCatalogEntry.name,
        settings: null, // Default Starting state for unconfigured Widget
        settingsVersion: { major: 1, minor: 0, patch: 0 }, // Default Starting state for unconfigured Widget
        size: widgetCatalogEntry.defaultSize,
        contributionId: widgetCatalogEntry.contributionId,
        position: {
            column: 0,
            row: 0
        },
        areSettingsBlockedForUser: false, // This is determined by server, and is trivally false when user is configuring.
        configurationContributionId: widgetCatalogEntry.configContributionId,
        isNameConfigurable: widgetCatalogEntry.isNameConfigurable,
        isEnabled: true, // trivially true at creation time, as contribution exists.
        eTag: ""
    };
}

VssComponent.register("WidgetCatalogView", WidgetCatalogView);
